import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => n === null || n === undefined ? "—" : "Rp " + Math.round(Number(n || 0)).toLocaleString("id-ID");
const fmtDT = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" }); } catch { return iso; } };
const todayISOWIB = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date());
const addDays = (iso, n) => { const d = new Date(iso+"T00:00:00"); d.setDate(d.getDate()+n); return d.toISOString().slice(0,10); };
function periodDates(p) {
  const today = todayISOWIB();
  if (p === "today") return { from: today, to: today };
  if (p === "week") {
    const d = new Date(today+"T00:00:00");
    const monday = new Date(d); monday.setDate(d.getDate() - ((d.getDay()+6)%7));
    return { from: monday.toISOString().slice(0,10), to: today };
  }
  if (p === "month") return { from: today.slice(0,8)+"01", to: today };
  if (p === "year") return { from: today.slice(0,4)+"-01-01", to: today };
  return { from: "", to: "" };
}

export function HistoryPanel() {
  const [type, setType] = useState("all"); // all | cancel | void
  const [period, setPeriod] = useState("all"); // all | today | week | month | year | range
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Sync period → from/to
  useEffect(() => {
    if (period === "all") { setFrom(""); setTo(""); return; }
    if (period === "range") return; // user pilih manual
    const r = periodDates(period);
    setFrom(r.from); setTo(r.to);
  }, [period]);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const qs = new URLSearchParams();
      if (type) qs.set("type", type);
      if (from) qs.set("from_", from);
      if (to) qs.set("to", to);
      const r = await axios.get(`${API}/history/changes?${qs.toString()}`, { withCredentials: true });
      setRows(r.data || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Gagal memuat riwayat");
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, from, to]);

  const periodLabel = useMemo(() => {
    if (period === "all") return "Semua Waktu";
    if (period === "today") return "Hari Ini";
    if (period === "week") return "Minggu Ini";
    if (period === "month") return "Bulan Ini";
    if (period === "year") return "Tahun Ini";
    return `${from||"-"} → ${to||"-"}`;
  }, [period, from, to]);

  return <div className="section" data-testid="history-panel">
    <div className="section-title">
      <h3>Riwayat Pembatalan & Koreksi</h3>
      <span className="muted small">{rows.length} entri · {periodLabel}</span>
    </div>
    <div className="perf-period" data-testid="history-period" style={{marginBottom:10}}>
      <div className="perf-period-label"><span>Periode</span><b>{periodLabel}</b></div>
      <div className="perf-period-chips" data-testid="history-period-chips">
        {[{id:"all",label:"Semua"},{id:"today",label:"Hari Ini"},{id:"week",label:"Minggu Ini"},{id:"month",label:"Bulan Ini"},{id:"year",label:"Tahun Ini"},{id:"range",label:"Rentang"}].map(o => <button
          key={o.id}
          type="button"
          className={period===o.id?"active":""}
          data-testid={`history-period-${o.id}`}
          onClick={()=>setPeriod(o.id)}
        >{o.label}</button>)}
      </div>
      {period === "range" && <div className="perf-range-inputs" data-testid="history-range-inputs">
        <label>Dari <input type="date" data-testid="history-from" value={from} onChange={e=>setFrom(e.target.value)}/></label>
        <label>Sampai <input type="date" data-testid="history-to" value={to} onChange={e=>setTo(e.target.value)}/></label>
      </div>}
    </div>
    <div className="order-filter-row" data-testid="history-filter-row" style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:12}}>
      <select data-testid="history-type-filter" value={type} onChange={e=>setType(e.target.value)} aria-label="Filter tipe riwayat">
        <option value="all">Semua Perubahan</option>
        <option value="cancel">Pembatalan Nota</option>
        <option value="void">Void/Koreksi Pembayaran</option>
      </select>
      <button type="button" data-testid="history-reload" className="soft-btn small-btn" onClick={load}>Muat Ulang</button>
    </div>
    {loading && <div className="empty" data-testid="history-loading">Memuat riwayat…</div>}
    {err && <div className="alert" data-testid="history-error">{err}</div>}
    {!loading && !err && rows.length === 0 && <div className="empty" data-testid="history-empty">Tidak ada riwayat pada rentang ini.</div>}
    {rows.length > 0 && <div className="table-wrap"><table data-testid="history-table">
      <thead><tr>
        <th>Waktu (WIB)</th>
        <th>Aksi</th>
        <th>Nota</th>
        <th>Pelanggan</th>
        <th style={{textAlign:"right"}}>Nominal</th>
        <th>Nilai Lama → Baru</th>
        <th>Alasan</th>
        <th>User</th>
      </tr></thead>
      <tbody>
        {rows.map((r, i) => {
          const isCancel = r.action === "Batalkan nota";
          return <tr key={r.id || i} data-testid={`history-row-${i}`}>
            <td>{fmtDT(r.at)}</td>
            <td>
              <span className={`status-chip ${isCancel?"status-cancel":"status-pending"}`} data-testid={`history-action-${i}`}>
                {isCancel ? "Nota Dibatalkan" : "Payment Void"}
              </span>
            </td>
            <td><b data-testid={`history-nota-${i}`}>{r.order_number || "—"}</b></td>
            <td data-testid={`history-customer-${i}`}>{r.customer_name || "—"}</td>
            <td style={{textAlign:"right",fontFamily:"'Space Mono',monospace"}} data-testid={`history-amount-${i}`}>{money(r.amount)}</td>
            <td>
              <div className="muted small" data-testid={`history-before-${i}`}>Lama: <b>{r.before?.status || "—"}{r.before?.amount!=null?` · ${money(r.before.amount)}`:""}</b></div>
              <div className="muted small" data-testid={`history-after-${i}`}>Baru: <b>{r.after?.status || "—"}{r.after?.amount!=null?` · ${money(r.after.amount)}`:""}</b></div>
            </td>
            <td data-testid={`history-reason-${i}`}>{r.reason || "—"}</td>
            <td data-testid={`history-user-${i}`}>{r.user || "—"}</td>
          </tr>;
        })}
      </tbody>
    </table></div>}
  </div>;
}
